import {
  formatIsoDate,
  formatName,
  renderStatus,
} from "../../../utils/generalUtils";

import GeneratePdf from "../../../generatePdf/GeneratePdf";
import { Button, Space, Tooltip } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import ViewingFormButton from "../../Form/ViewingFormButton";

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatAbsentDatesLabel(absentDates) {
  if (!Array.isArray(absentDates) || absentDates.length === 0) return "None";
  // New submissions store a single free-text string; old ones store ISO date strings.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(absentDates[0])) {
    return absentDates[0] || "None";
  }
  return absentDates
    .map((ds) => {
      const d = new Date(ds + "T00:00:00");
      return `${SHORT_MONTHS[d.getMonth()]} ${d.getDate()}`;
    })
    .join(", ");
}

function formatAccomplishmentsLabel(accomplishments) {
  if (!Array.isArray(accomplishments) || accomplishments.length === 0) return "—";
  return (
    <ul style={{ margin: 0, paddingLeft: 20, maxHeight: 240, overflowY: "auto" }}>
      {accomplishments.map((a, i) => (
        <li key={i}>{a}</li>
      ))}
    </ul>
  );
}

// Period/Accomplishments shown both in the pre-submit review and the
// post-submission preview.
const baseViewingDataConfig = [
  {
    key: 1,
    collapseName: "Period",
    collapseItems: [
      { key: 1, name: "start_date", label: "Start Date" },
      { key: 2, name: "end_date", label: "End Date" },
      {
        key: 3,
        name: "absent_dates",
        label: "Dates of Absence",
        render: (val) =>
          Array.isArray(val) ? formatAbsentDatesLabel(val) : val || "None",
      },
    ],
  },
  {
    key: 2,
    collapseName: "Accomplishments",
    collapseItems: [
      {
        key: 1,
        name: "accomplishments",
        label: "Accomplishments",
        render: (val) => formatAccomplishmentsLabel(val),
      },
    ],
  },
];

export const cwaConfig = {
  docType: "Certificate of Work Accomplishment",
  title: "Certificate of Work Accomplishment",
  description:
    "Submit your Certificate of Work Accomplishment for the service period. List the tasks you completed under your contract of services. Your submission will be reviewed by your assigned approver. Once approved, you may download the signed certificate.",
  popupWidth: 900,
  apiConfig: {
    get: "/employee/cwa",
    post: "/employee/addcwa",
    update: "/employee/updatecwa",
    cancel: "/employee/cancel/cwa",
  },
  dataMapper: (item, index) => ({
    ...item,
    name: `${item?.submission?.submitter?.last_name || ""}, ${
      item?.submission?.submitter?.first_name || ""
    }`.trim(),
    formattedName: formatName(
      item?.submission?.submitter?.first_name,
      item?.submission?.submitter?.middle_name,
      item?.submission?.submitter?.last_name,
      "lfmcut"
    ),
    approver_name: `${item?.submission?.approval?.[0]?.approver?.last_name || ""} ${
      item?.submission?.approval?.[0]?.approver?.first_name || ""
    }`.trim(),
    formattedApproverName: formatName(
      item?.submission?.approval?.[0]?.approver?.first_name,
      item?.submission?.approval?.[0]?.approver?.middle_name,
      item?.submission?.approval?.[0]?.approver?.last_name,
      "lfmcut"
    ),
    approver_position_name:
      item?.submission?.approval?.[0]?.position?.position_name ?? "",
    approver_position_unit:
      item?.submission?.approval?.[0]?.position?.unit ?? "",
    submitter_position: item?.submission?.position?.position_name,
    submitter_position_unit: item?.submission?.position?.unit,
    submitter_email: item?.submission?.submitter?.email,
    approval_date: item?.submission?.approval?.[0]?.approval_date,
    remarks: item?.remarks,
    approver_remarks: item?.submission?.remarks,
    date_submitted: item?.created_at ? formatIsoDate(item?.created_at) : null,
    key: index + 1,
    status: item?.submission?.status,
  }),
  columns: (onDeleted, onEdit) => [
    { title: "No.", dataIndex: "key", rowScope: "row", width: 60 },
    {
      title: "Position",
      dataIndex: "submitter_position",
      key: "submitter_position",
    },
    { title: "Start Date", dataIndex: "start_date", key: "start_date" },
    { title: "End Date", dataIndex: "end_date", key: "end_date" },
    /* TEMP: Remarks column hidden until feature is reviewed by boss
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 160,
      ellipsis: true,
      render: (val) => val || "—",
    },
    */
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus(
        ["pending"],
        ["approved"],
        ["disapproved", "denied"],
        ["cancelled"]
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) => (
        <Space>
          <GeneratePdf
            docuType="cwa"
            contents={item}
            disabled={item?.status !== "approved"}
            isEmployee={true}
            isIcon={true}
          />
          <ViewingFormButton
            contents={item}
            viewingDataConfig={cwaConfig.postSubmissionViewingDataConfig}
            isIcon={true}
          />
          <Tooltip
            title={
              item?.status === "pending"
                ? "Edit or cancel this submission"
                : "Only pending submissions can be edited"
            }
          >
            <Button
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
              disabled={item?.status !== "pending"}
              onClick={() => onEdit?.(item)}
              style={
                item?.status === "pending"
                  ? {
                      color: "white",
                      backgroundColor: "#FA8C16",
                      borderColor: "#FA8C16",
                    }
                  : {}
              }
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Date Submitted",
      dataIndex: "date_submitted",
      key: "date_submitted",
    },
  ],
  submitTransform: (values) => ({
    ...values,
    absent_dates:
      typeof values.absent_dates === "string" && values.absent_dates.trim()
        ? [values.absent_dates]
        : [],
    remarks: values.remarks?.trim() || null,
  }),
  // Reverse of submitTransform — maps a stored submission back into edit-form values.
  toFormValues: (item) => ({
    date:
      item?.start_date && item?.end_date
        ? [dayjs(item.start_date), dayjs(item.end_date)]
        : undefined,
    start_date: item?.start_date,
    end_date: item?.end_date,
    absent_dates: Array.isArray(item?.absent_dates)
      ? item.absent_dates[0] || ""
      : item?.absent_dates || "",
    accomplishments: Array.isArray(item?.accomplishments)
      ? item.accomplishments
      : [],
  }),
  formFields: [
    {
      name: "date",
      label: "Period",
      type: "daterange",
      column: "left",
      rules: [{ required: true, message: "Please select a period", type: "array" }],
    },
    {
      name: "absent_dates",
      label: "Dates of Absence",
      type: "textarea",
      column: "left",
      placeholder: "e.g. Mar 3 (half day), Mar 10 (early out 4pm)",
      extra: "Indicate any absences, half days, or early outs during the period.",
      maxLength: 1000,
      showCount: true,
    },
    /* TEMP: Remarks form field hidden until feature is reviewed by boss
    {
      name: "remarks",
      label: "Remarks",
      type: "textarea",
      column: "left",
      placeholder: "Optional remarks (max 30 words)",
      extra: "Brief remarks about this submission.",
      maxLength: 300,
      showCount: true,
    },
    */
    {
      name: "accomplishments",
      label: "Accomplishments",
      type: "accomplishments-list",
      column: "right",
      rules: [
        {
          required: true,
          message: "Please add at least one accomplishment",
          validator: (_, value) => {
            if (
              Array.isArray(value) &&
              value.length > 0 &&
              value.every((v) => v && v.trim())
            ) {
              return Promise.resolve();
            }
            return Promise.reject(
              new Error("Please add at least one accomplishment")
            );
          },
        },
      ],
    },
  ],
  // Used for the pre-submit review modal (PvForm) — no remarks yet at this point.
  viewingDataConfig: baseViewingDataConfig,
  // Used for the post-submission preview (ViewingFormButton) — adds the approver's remarks.
  postSubmissionViewingDataConfig: [
    ...baseViewingDataConfig,
    /* TEMP: Submitter's own remarks (entered at submission time) hidden
       until feature is reviewed by boss. May also be added to the PDF later.
    {
      key: 3,
      collapseName: "Remarks",
      collapseItems: [
        { key: 1, name: "remarks", label: "Remarks", render: (val) => val || "None" },
      ],
    },
    */
    {
      key: 4,
      collapseName: "Approver Remarks",
      collapseItems: [
        {
          key: 1,
          name: "approver_remarks",
          label: "Remarks",
          render: (val) => val || "None",
        },
      ],
    },
  ],
};
